/**
 * AdminCategories.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from './ToastContainer';
import './AdminCategories.css';

const PRESET_ICONS = [
  '📖', '💿', '📦', '🎬', '🎵', '☕', '🎧', '📱', '💻', '🖥️',
  '⌨️', '🖱️', '🎮', '🎯', '🎨', '🖼️', '🎭', '🎪', '🎡', '🎢',
  '🎤', '🎸', '🎹', '🎺', '🎻', '🥁', '🎷', '🎼', '🎧', '📻',
  '📺', '📷', '📸', '📹', '🎥', '📽️', '🎬', '📚', '📓', '📕',
  '📗', '📘', '📙', '📔', '📒', '📰', '🗞️', '📄', '📃', '📑',
  '🔖', '🏷️', '💳', '🎁', '🎀', '🎊', '🎉', '🎈', '🎀', '🏆'
];

const AdminCategories = () => {
  const { showSuccess, showError } = useToast();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [formState, setFormState] = useState({
    id: '',
    name: '',
    icon: '📦',
    display_order: 0,
    tax_type: 'auto',        // 'auto', 'gst', 'hst', 'custom', 'exempt'
    tax_rate: '',            // Custom tax rate percentage
    tax_notes: ''            // Internal notes about tax configuration
  });
  const [showIconPicker, setShowIconPicker] = useState(false);

  // Get token
  const getToken = () => {
    return localStorage.getItem('token');
  };

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch('/api/categories');
      const data = await response.json();
      setCategories(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching categories:', error);
      showError('Failed to load categories');
      setLoading(false);
    }
  }, [showError]);

  // Load categories
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleIconSelect = (icon) => {
    setFormState(prev => ({ ...prev, icon }));
    setShowIconPicker(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formState.name || !formState.icon) {
      showError('Name and icon are required');
      return;
    }

    try {
      const method = isEditing ? 'PUT' : 'POST';
      const url = isEditing ? `/api/categories/${formState.id}` : '/api/categories';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          name: formState.name,
          icon: formState.icon,
          display_order: parseInt(formState.display_order) || 0
        })
      });

      if (response.ok) {
        showSuccess(isEditing ? 'Category updated!' : 'Category added!');
        fetchCategories();
        handleCancel();
      } else {
        const error = await response.json();
        showError(error.error || 'Failed to save category');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      showError('Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setFormState({
      id: category.id,
      name: category.name,
      icon: category.icon,
      display_order: category.display_order || 0
    });
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      const response = await fetch(`/api/categories/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        showSuccess('Category deleted!');
        fetchCategories();
      } else {
        const error = await response.json();
        showError(error.error || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      showError('Failed to delete category');
    }
  };

  const handleCancel = () => {
    setFormState({ id: '', name: '', icon: '📦', display_order: 0 });
    setIsEditing(false);
    setIsAdding(false);
  };

  if (loading) {
    return <div className="admin-categories-loading">Loading categories...</div>;
  }

  return (
    <div className="admin-categories-container">
      <div className="page-header">
        <div className="header-left">
          <h2>📂 Product Categories</h2>
          <span className="category-count-badge">{categories.length} categories</span>
        </div>
        <button 
          className="add-category-button"
          onClick={() => setIsAdding(true)}
        >
          + Add Category
        </button>
      </div>

      {/* Add/Edit Form */}
      {(isAdding || isEditing) && (
        <div className="category-form-modal">
          <div className="category-form-content">
            <div className="form-header">
              <h3>{isEditing ? 'Edit Category' : 'Add New Category'}</h3>
              <button onClick={handleCancel} className="close-button">×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="category-form">
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formState.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Coffee, MP3, Merchandise"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label>Icon *</label>
                <div className="icon-selector">
                  <button
                    type="button"
                    className="icon-display-button"
                    onClick={() => setShowIconPicker(!showIconPicker)}
                  >
                    <span className="selected-icon">{formState.icon}</span>
                    <span className="icon-label">Click to change</span>
                  </button>

                  {showIconPicker && (
                    <div className="icon-picker">
                      <div className="icon-picker-header">Select an Icon</div>
                      <div className="icon-grid">
                        {PRESET_ICONS.map((icon, index) => (
                          <button
                            key={index}
                            type="button"
                            className={`icon-option ${formState.icon === icon ? 'selected' : ''}`}
                            onClick={() => handleIconSelect(icon)}
                          >
                            {icon}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  name="display_order"
                  value={formState.display_order}
                  onChange={handleInputChange}
                  placeholder="0"
                  className="form-input"
                  min="0"
                />
                <small className="field-hint">Lower numbers appear first</small>
              </div>

              {/* Tax Configuration Section */}
              <div className="tax-config-section" style={{
                marginTop: '1.5rem',
                padding: '1.5rem',
                backgroundColor: 'var(--color-background)',
                borderRadius: '8px',
                border: '1px solid var(--color-border)'
              }}>
                <h4 style={{ 
                  margin: '0 0 1rem 0', 
                  color: 'var(--color-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '1.1rem'
                }}>
                  💰 Default Tax Configuration
                  <span style={{ fontSize: '0.75rem', fontWeight: 'normal', color: 'var(--color-text-secondary)' }}>
                    (Applied to all products in this category)
                  </span>
                </h4>

                <div className="form-group">
                  <label>Tax Type</label>
                  <select
                    name="tax_type"
                    value={formState.tax_type || 'auto'}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="auto">🤖 Auto (CRA Rules) - Books get 5% GST, others use province rate</option>
                    <option value="gst">📚 GST Only (5%) - Point-of-Sale Rebate</option>
                    <option value="hst">🏢 Full HST - Use Provincial Rate (13-15%)</option>
                    <option value="custom">⚙️ Custom Rate - Override with specific percentage</option>
                    <option value="exempt">🆓 Tax Exempt - No tax charged</option>
                  </select>
                  <small className="field-hint" style={{ color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>
                    {formState.tax_type === 'auto' && '✓ Recommended: Automatically applies CRA point-of-sale rebate rules'}
                    {formState.tax_type === 'gst' && '📚 All products in this category will charge 5% GST only'}
                    {formState.tax_type === 'hst' && '⚠️ All products will charge full provincial HST rate'}
                    {formState.tax_type === 'custom' && '⚙️ Set a custom tax rate below (use with caution)'}
                    {formState.tax_type === 'exempt' && '⚠️ No tax will be charged (ensure CRA compliance)'}
                  </small>
                </div>

                {formState.tax_type === 'custom' && (
                  <div className="form-group">
                    <label>Custom Tax Rate (%)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="number"
                        name="tax_rate"
                        value={formState.tax_rate || ''}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="e.g., 5, 13, 15"
                        step="0.01"
                        min="0"
                        max="100"
                        style={{ maxWidth: '150px' }}
                      />
                      <span>%</span>
                    </div>
                    <small className="field-hint" style={{ color: 'var(--color-error)' }}>
                      ⚠️ Custom rates override CRA rules. Ensure compliance before using.
                    </small>
                  </div>
                )}

                <div className="form-group">
                  <label>Tax Configuration Notes (Internal)</label>
                  <textarea
                    name="tax_notes"
                    value={formState.tax_notes || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    rows="2"
                    placeholder="e.g., 'Books qualify for point-of-sale rebate per CRA guidelines'"
                    style={{ resize: 'vertical', fontFamily: 'inherit' }}
                  />
                  <small className="field-hint" style={{ color: 'var(--color-text-secondary)' }}>
                    Optional: Document why this tax configuration was chosen (for audit purposes)
                  </small>
                </div>

                {/* Tax Preview */}
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  backgroundColor: 'var(--color-surface)',
                  borderRadius: '6px',
                  border: '1px solid var(--color-border)'
                }}>
                  <h5 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: 'var(--color-primary)' }}>
                    🔍 Tax Preview (Ontario Example):
                  </h5>
                  <div style={{ fontSize: '0.85rem', lineHeight: '1.6', color: 'var(--color-text-primary)' }}>
                    {formState.tax_type === 'auto' && (
                      <p style={{ margin: 0 }}>
                        {formState.name && formState.name.toLowerCase().includes('book') ? (
                          <span style={{ color: 'var(--color-success)' }}>
                            ✓ <strong>Books: 5% GST</strong> (Point-of-Sale Rebate Applied)<br/>
                            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                              Customer saves 8% vs full 13% HST in Ontario
                            </span>
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-primary)' }}>
                            <strong>Non-Book: 13% HST</strong> (Full provincial rate in Ontario)
                          </span>
                        )}
                      </p>
                    )}
                    {formState.tax_type === 'gst' && (
                      <p style={{ margin: 0, color: 'var(--color-success)' }}>
                        ✓ <strong>5% GST Only</strong> (Provincial portion rebated)
                      </p>
                    )}
                    {formState.tax_type === 'hst' && (
                      <p style={{ margin: 0, color: 'var(--color-primary)' }}>
                        <strong>13% HST</strong> (Full rate - 5% GST + 8% PST in Ontario)
                      </p>
                    )}
                    {formState.tax_type === 'custom' && formState.tax_rate && (
                      <p style={{ margin: 0, color: 'var(--color-warning)' }}>
                        ⚙️ <strong>{formState.tax_rate}% Custom Rate</strong>
                      </p>
                    )}
                    {formState.tax_type === 'exempt' && (
                      <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
                        🆓 <strong>Tax Exempt</strong> (0% - No tax charged)
                      </p>
                    )}
                  </div>
                </div>

                {/* CRA Reference */}
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  backgroundColor: 'var(--color-surface)',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  color: 'var(--color-text-secondary)'
                }}>
                  <strong>📚 CRA Reference:</strong> <a 
                    href="https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/gst-hst-businesses/charge-collect-specific-situations/suppliers-publications.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--color-primary)' }}
                  >
                    GST/HST for Suppliers of Publications
                  </a>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={handleCancel} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" className="submit-button">
                  {isEditing ? 'Update Category' : 'Add Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Categories List */}
      <div className="categories-grid">
        {categories.map(category => (
          <div key={category.id} className="category-card">
            <div className="category-icon">{category.icon}</div>
            <div className="category-info">
              <div className="category-name">{category.name}</div>
              <div className="category-order">Order: {category.display_order}</div>
            </div>
            <div className="category-actions">
              <button
                onClick={() => handleEdit(category)}
                className="edit-button"
                title="Edit category"
              >
                ✏️
              </button>
              <button
                onClick={() => handleDelete(category.id)}
                className="delete-button"
                title="Delete category"
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
      </div>

      {categories.length === 0 && (
        <div className="no-categories">
          <p>No categories yet. Click "Add Category" to get started.</p>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;

